import { useEffect, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Package as PackageIcon, TrendingDown, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const emptyForm = { name: "", description: "", category: "seasonal",
                    job_type_ids: [], parts: [], labour_total: 0, original_labour: 0, active: true };

export default function Packages() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [parts, setParts] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [selPart, setSelPart] = useState("");
  const [selQty, setSelQty] = useState(1);

  const canEdit = ["owner", "manager"].includes(user.role);

  const load = () => api.get("/packages").then(r => setList(r.data));
  useEffect(() => {
    load();
    api.get("/job-types").then(r => setJobTypes(r.data)).catch(() => {});
    api.get("/parts").then(r => setParts(r.data)).catch(() => {});
  }, []);

  const suggestedLabour = f.job_type_ids.reduce(
    (s, id) => s + (jobTypes.find(j => j.id === id)?.labour_rate || 0), 0
  );

  useEffect(() => {
    if (!editId && suggestedLabour) {
      setF(x => ({ ...x, original_labour: suggestedLabour, labour_total: x.labour_total || suggestedLabour }));
    }
    // eslint-disable-next-line
  }, [suggestedLabour, editId]);

  const toggleJobType = (id) => setF(x => ({
    ...x,
    job_type_ids: x.job_type_ids.includes(id) ? x.job_type_ids.filter(j => j !== id) : [...x.job_type_ids, id],
  }));

  const addPart = () => {
    const p = parts.find(x => x.id === selPart);
    if (!p) return;
    setF(x => ({ ...x, parts: [...x.parts, { part_id: p.id, name: p.name, qty: Number(selQty), price: p.price }]}));
    setSelPart(""); setSelQty(1);
  };

  const save = async () => {
    try {
      const payload = { ...f, labour_total: Number(f.labour_total), original_labour: Number(f.original_labour) || null };
      if (editId) await api.patch(`/packages/${editId}`, payload);
      else await api.post("/packages", payload);
      toast.success(editId ? "Package updated" : "Package added");
      setF(emptyForm); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = (p) => {
    setF({ name: p.name, description: p.description || "", category: p.category,
           job_type_ids: p.job_type_ids || [], parts: p.parts || [],
           labour_total: p.labour_total, original_labour: p.original_labour || 0, active: p.active !== false });
    setEditId(p.id); setOpen(true);
  };

  const del = async (id) => {
    if (!confirm("Delete this package?")) return;
    try { await api.delete(`/packages/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Sparkles className="w-3 h-3"/> One-tap on job cards</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Service Packages</h1>
          <div className="text-sm text-muted-foreground mt-2">Bundle jobs + parts at a discounted labour rate</div>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(emptyForm); setEditId(null); }}}>
            <DialogTrigger asChild>
              <Button data-testid="add-package-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
                <Plus className="w-4 h-4 mr-1"/> New Package
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Package</DialogTitle></DialogHeader>
              <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Name</Label>
                    <Input data-testid="pkg-name" value={f.name} onChange={(e) => setF(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="e.g. Monsoon Service Pack" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description</Label>
                    <Textarea data-testid="pkg-desc" value={f.description} onChange={(e) => setF(x => ({ ...x, description: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Category</Label>
                    <Select value={f.category} onValueChange={(v) => setF(x => ({ ...x, category: v }))}>
                      <SelectTrigger className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="seasonal">Seasonal</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="trip">Trip</SelectItem>
                        <SelectItem value="general">General</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Original ₹</Label>
                      <Input data-testid="pkg-original" type="number" value={f.original_labour} onChange={(e) => setF(x => ({ ...x, original_labour: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-primary">Bundle ₹</Label>
                      <Input data-testid="pkg-labour" type="number" value={f.labour_total} onChange={(e) => setF(x => ({ ...x, labour_total: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-primary/40" />
                    </div>
                  </div>
                </div>

                <div className="border border-border rounded-sm p-3">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2 block">Included Jobs · sum ₹{suggestedLabour}</Label>
                  <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto">
                    {jobTypes.map(jt => (
                      <label key={jt.id} data-testid={`pkg-jt-${jt.id}`} className="flex items-center gap-2 p-1 text-sm cursor-pointer hover:bg-secondary rounded-sm">
                        <Checkbox checked={f.job_type_ids.includes(jt.id)} onCheckedChange={() => toggleJobType(jt.id)} />
                        <span className="truncate flex-1">{jt.name}</span>
                        <span className="font-mono text-xs text-muted-foreground">₹{jt.labour_rate}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="border border-border rounded-sm p-3">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2 block">Included Parts (pre-added on job card)</Label>
                  <div className="flex gap-2 mb-2">
                    <Select value={selPart} onValueChange={setSelPart}>
                      <SelectTrigger className="rounded-sm bg-secondary border-border flex-1"><SelectValue placeholder="Select part"/></SelectTrigger>
                      <SelectContent>{parts.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({inr(p.price)})</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" value={selQty} onChange={(e) => setSelQty(e.target.value)} className="w-20 rounded-sm bg-secondary border-border" />
                    <Button type="button" onClick={addPart} className="rounded-sm bg-secondary hover:bg-muted">Add</Button>
                  </div>
                  {f.parts.map((p, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-border py-1 text-sm">
                      <span>{p.name} × {p.qty}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs">{inr(p.qty * p.price)}</span>
                        <Button size="sm" variant="ghost" onClick={() => setF(x => ({ ...x, parts: x.parts.filter((_, j) => j !== i) }))}><Trash2 className="w-3 h-3"/></Button>
                      </div>
                    </div>
                  ))}
                </div>

                <Button data-testid="save-package-btn" onClick={save} disabled={!f.name || !f.labour_total} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
                  {editId ? "Update Package" : "Save Package"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {list.length === 0 && <div className="border border-border bg-card p-8 rounded-sm text-center text-muted-foreground">No packages yet.</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map(p => {
          const savings = p.original_labour && p.original_labour > p.labour_total ? p.original_labour - p.labour_total : 0;
          const jobs = (p.job_type_ids || []).map(id => jobTypes.find(j => j.id === id)).filter(Boolean);
          return (
            <div key={p.id} data-testid={`package-${p.id}`} className="border border-border bg-card p-5 rounded-sm hover:border-primary/40 transition-colors group relative">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-primary/10 border border-primary/40 text-primary">
                  <PackageIcon className="w-5 h-5"/>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary px-2 py-0.5 rounded-sm">{p.category}</span>
              </div>
              <div className="font-display font-black text-xl uppercase tracking-tight leading-tight">{p.name}</div>
              {p.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</div>}

              <div className="mt-4 flex items-baseline gap-2">
                <div className="font-display font-black text-3xl text-primary">{inr(p.labour_total)}</div>
                {savings > 0 && (
                  <>
                    <div className="text-sm text-muted-foreground line-through font-mono">{inr(p.original_labour)}</div>
                    <div className="text-[10px] uppercase tracking-wider text-green-500 bg-green-500/10 px-1.5 py-0.5 rounded-sm flex items-center gap-1"><TrendingDown className="w-3 h-3"/> save {inr(savings)}</div>
                  </>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-border space-y-1">
                {jobs.slice(0, 4).map(j => (
                  <div key={j.id} className="text-xs text-muted-foreground flex items-center gap-2">
                    <span className="w-1 h-1 bg-primary"/> {j.name}
                  </div>
                ))}
                {jobs.length > 4 && <div className="text-[10px] text-muted-foreground">+{jobs.length - 4} more</div>}
                {(p.parts || []).length > 0 && (
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-2">Includes {(p.parts || []).length} part(s)</div>
                )}
              </div>

              {canEdit && (
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <Button data-testid={`edit-pkg-${p.id}`} size="sm" variant="ghost" onClick={() => edit(p)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`del-pkg-${p.id}`} size="sm" variant="ghost" onClick={() => del(p.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
